import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BaseUrl from '../../../Url/BaseUrl';

const ViewTimetablePage = () => {
  const navigate = useNavigate();
  const [timetableData, setTimetableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    busStandCode: '',
    busNo: '',
    status: ''
  });
  const [busOptions, setBusOptions] = useState([]);
  const [busStandOptions, setBusStandOptions] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    arrivalTime: '',
    departureTime: '',
    status: true
  });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchTimetableData();
    fetchFilterOptions();
  }, []);

  const fetchTimetableData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`
      );
      setTimetableData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error('Failed to fetch timetable:', error);
      alert('Failed to fetch timetable data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [busesResponse, standsResponse] = await Promise.all([
        axios.get(`${BaseUrl.baseurl}Buses`),
        axios.get(`${BaseUrl.baseurl}BusStands`)
      ]);

      setBusOptions(busesResponse.data);
      setBusStandOptions(standsResponse.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...timetableData];

    if (filters.busStandCode) {
      filtered = filtered.filter(item => item.busStandCode === filters.busStandCode);
    }

    if (filters.busNo) {
      filtered = filtered.filter(item => item.busNo === parseInt(filters.busNo));
    }

    if (filters.status !== '') {
      filtered = filtered.filter(item => item.status === (filters.status === 'true'));
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, timetableData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      busStandCode: '',
      busNo: '',
      status: ''
    });
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    
    // Format time for input (HH:mm format)
    let arrivalTime = '';
    let departureTime = '';
    
    if (record.arrivalTime) {
      arrivalTime = record.arrivalTime.substring(0, 5); // Extract HH:mm from HH:mm:ss
    }
    
    if (record.departureTime) {
      departureTime = record.departureTime.substring(0, 5); // Extract HH:mm from HH:mm:ss
    }
    
    setEditFormData({
      arrivalTime,
      departureTime,
      status: record.status || true
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdate = async () => {
    if (!editingRecord) return;
    
    // Validate times
    if (editFormData.arrivalTime && editFormData.departureTime) {
      const arrival = new Date(`1970-01-01T${editFormData.arrivalTime}`);
      const departure = new Date(`1970-01-01T${editFormData.departureTime}`);
      if (arrival >= departure) {
        alert('Departure time must be after arrival time');
        return;
      }
    }
    
    setEditLoading(true);
    
    try {
      // Create payload matching API format
      const payload = {
        id: editingRecord.id,
        busStandCode: editingRecord.busStandCode,
        busStand: null,
        busNo: editingRecord.busNo,
        bus: null,
        arrivalTime: editFormData.arrivalTime ? `${editFormData.arrivalTime}:00` : null,
        departureTime: editFormData.departureTime ? `${editFormData.departureTime}:00` : null,
        status: editFormData.status,
        baab: editingRecord.baab || 0 // Add baab field if it exists
      };
      
      console.log('Updating with payload:', payload);

      const response = await axios.put(
        `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands/${editingRecord.id}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Update response:', response.data);
      alert('Timetable entry updated successfully');
      setShowEditModal(false);
      fetchTimetableData(); // Refresh data
    } catch (error) {
      console.error('Update error:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 400) {
        if (error.response.data?.errors) {
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          alert(`Validation failed:\n${errorMessages}`);
        } else if (error.response.data?.title) {
          alert(`${error.response.data.title}: ${error.response.data.detail || 'Please check your input'}`);
        } else {
          alert('Failed to update timetable entry. Please check the data format.');
        }
      } else {
        alert('Failed to update timetable entry');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this timetable entry?')) {
      try {
        await axios.delete(`${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands/${id}`);
        alert('Timetable entry deleted successfully');
        fetchTimetableData();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete timetable entry');
      }
    }
  };

  const formatTime = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
      return 'N/A';
    }
    
    try {
      // If time is already in HH:mm format
      if (timeString.length === 5 && timeString.includes(':')) {
        return timeString;
      }
      
      // If time is in HH:mm:ss format
      if (timeString.length >= 8 && timeString.includes(':')) {
        return timeString.substring(0, 5); // Return HH:mm
      }
      
      // Try to parse ISO string
      if (timeString.includes('T')) {
        const timePart = timeString.split('T')[1];
        if (timePart) {
          return timePart.substring(0, 5);
        }
      }
      
      return timeString.substring(0, 5) || 'N/A';
    } catch (error) {
      console.error('Error formatting time:', error, timeString);
      return 'N/A';
    }
  };

  const getBusDetails = (busNo) => {
    if (!busNo) return 'N/A';
    
    const bus = busOptions.find(b => b.busNo === busNo || b.busNo === parseInt(busNo));
    if (bus) {
      return `${bus.busName || 'Unknown'} (${bus.busRegistrationNumber || 'No Reg'})`;
    }
    return 'N/A';
  };

  const getBusStandName = (busStandCode) => {
    if (!busStandCode) return 'N/A';
    
    const stand = busStandOptions.find(s => s.busStandCode === busStandCode);
    return stand ? stand.busStandName : 'N/A';
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3', 
          borderTop: '4px solid #1890ff', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <p>Loading timetable data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Back Button */}
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="btn-back"
        >
          ← Back
        </button>
      </div>

      <div style={{ 
        background: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', 
        overflow: 'hidden' 
      }}>
        <div style={{ 
          background: '#FFFDE7', 
          color: 'white', 
          padding: '16px 24px' 
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: 'black' }}>Bus Timetable Management</h2>
        </div>
        
        {/* Filters */}
        <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Filters</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                Bus Stand
              </label>
              <select
                name="busStandCode"
                value={filters.busStandCode}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px', 
                  fontSize: '14px' 
                }}
              >
                <option value="">All Bus Stands</option>
                {busStandOptions.map(stand => (
                  <option key={stand.busStandCode} value={stand.busStandCode}>
                    {stand.busStandName} ({stand.busStandCode})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                Bus Number
              </label>
              <select
                name="busNo"
                value={filters.busNo}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px', 
                  fontSize: '14px' 
                }}
              >
                <option value="">All Buses</option>
                {busOptions.map(bus => (
                  <option key={bus.busNo} value={bus.busNo}>
                    {bus.busNo} - {bus.busName}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px', 
                  fontSize: '14px' 
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button 
              style={{ 
                background: '#f5f5f5', 
                color: '#333', 
                border: '1px solid #d9d9d9', 
                padding: '8px 16px', 
                borderRadius: '4px', 
                fontSize: '14px', 
                cursor: 'pointer' 
              }}
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
            <button 
              style={{ 
                background: '#52c41a', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '4px', 
                fontSize: '14px', 
                cursor: 'pointer' 
              }}
              onClick={fetchTimetableData}
            >
              ↻ Refresh Data
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ padding: '20px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#fafafa' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    ID
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Bus Stand
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Bus Details
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Arrival Time
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Departure Time
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#333', borderBottom: '2px solid #f0f0f0' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                      No timetable entries found
                    </td>
                  </tr>
                ) : (
                  filteredData.map(record => (
                    <tr key={record.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>{record.id}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontWeight: '500' }}>{getBusStandName(record.busStandCode)}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{record.busStandCode}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontWeight: '500' }}>Bus No: {record.busNo}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{getBusDetails(record.busNo)}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px',fontFamily: "'Noto Serif Devanagari', serif", fontWeight: '500' }}>
                        {formatTime(record.arrivalTime)}
                      </td>
                      <td style={{ padding: '12px',fontFamily: "'Noto Serif Devanagari', serif", fontWeight: '500' }}>
                        {formatTime(record.departureTime)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px', 
                          fontWeight: '500',
                          background: record.status ? '#f6ffed' : '#fff2f0',
                          color: record.status ? '#52c41a' : '#ff4d4f',
                          border: `1px solid ${record.status ? '#b7eb8f' : '#ffccc7'}`
                        }}>
                          {record.status ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            style={{ 
                              background: '#faad14', 
                              color: 'white', 
                              border: 'none', 
                              padding: '4px 8px', 
                              borderRadius: '4px', 
                              fontSize: '12px', 
                              cursor: 'pointer' 
                            }}
                            onClick={() => handleEdit(record)}
                            title="Edit"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            style={{ 
                              background: '#ff4d4f', 
                              color: 'white', 
                              border: 'none', 
                              padding: '4px 8px', 
                              borderRadius: '4px', 
                              fontSize: '12px', 
                              cursor: 'pointer' 
                            }}
                            onClick={() => handleDelete(record.id)}
                            title="Delete"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0', color: '#666', fontSize: '14px' }}>
            <p>Showing {filteredData.length} of {timetableData.length} entries</p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '8px', 
            width: '90%', 
            maxWidth: '500px', 
            maxHeight: '90vh', 
            overflow: 'auto' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '16px 24px', 
              borderBottom: '1px solid #f0f0f0' 
            }}>
              <h3 style={{ margin: 0 }}>Edit Timetable Entry</h3>
              <button 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer', 
                  color: '#666' 
                }}
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f0f0f0' }}>
                <p><strong>Bus Stand:</strong> {getBusStandName(editingRecord?.busStandCode)}</p>
                <p><strong>Bus:</strong> {editingRecord?.busNo} - {getBusDetails(editingRecord?.busNo)}</p>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Arrival Time</label>
                  <input
                    type="time"
                    name="arrivalTime"
                    value={editFormData.arrivalTime}
                    onChange={handleEditInputChange}
                    step="300"
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: '4px', 
                      fontSize: '14px' 
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Departure Time</label>
                  <input
                    type="time"
                    name="departureTime"
                    value={editFormData.departureTime}
                    onChange={handleEditInputChange}
                    step="300"
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: '4px', 
                      fontSize: '14px' 
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="status"
                      checked={editFormData.status}
                      onChange={handleEditInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #f0f0f0', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px' 
            }}>
              <button 
                style={{ 
                  background: '#f5f5f5', 
                  color: '#333', 
                  border: '1px solid #d9d9d9', 
                  padding: '8px 16px', 
                  borderRadius: '4px', 
                  fontSize: '14px', 
                  cursor: 'pointer' 
                }}
                onClick={() => setShowEditModal(false)}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button 
                style={{ 
                  background: '#1890ff', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 16px', 
                  borderRadius: '4px', 
                  fontSize: '14px', 
                  cursor: 'pointer',
                  opacity: editLoading ? 0.6 : 1
                }}
                onClick={handleUpdate}
                disabled={editLoading}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx="true">{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ViewTimetablePage;